/**
 * Image Cache Service
 * 
 * Caches signed URLs for Supabase Storage images to avoid regenerating them
 * on every component mount. Uses both in-memory cache and browser Cache API
 * for persistent caching across page reloads.
 */

interface CachedImage {
  signedUrl: string;
  expiresAt: number; // Timestamp in milliseconds
  originalUrl: string;
}

// In-memory cache (fast access)
const memoryCache = new Map<string, CachedImage>();

// Cache API for persistent browser-level caching
let cacheApi: Cache | null = null;

// Initialize Cache API
if (typeof window !== 'undefined' && 'caches' in window) {
  caches.open('image-cache-v1').then((cache) => {
    cacheApi = cache;
  }).catch((err) => {
    console.warn('Failed to open Cache API:', err);
  });
}

/**
 * Generate cache key from authenticated URL
 */
function getCacheKey(authenticatedUrl: string, expiresIn: number): string {
  return `${authenticatedUrl}:${expiresIn}`;
}

/**
 * Check if cached URL is still valid (not expired)
 */
function isCacheValid(cached: CachedImage): boolean {
  // Consider cache valid if it expires in more than 5 minutes
  // This gives us a buffer before the actual expiration
  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  return cached.expiresAt > Date.now() + bufferTime;
}

/**
 * Get cached signed URL from memory cache
 */
function getFromMemoryCache(key: string): CachedImage | null {
  const cached = memoryCache.get(key);
  if (cached && isCacheValid(cached)) {
    return cached;
  }
  // Remove expired entry
  if (cached) {
    memoryCache.delete(key);
  }
  return null;
}

/**
 * Store in memory cache
 */
function setMemoryCache(key: string, cached: CachedImage): void {
  memoryCache.set(key, cached);
  
  // Clean up expired entries periodically (keep cache size manageable)
  if (memoryCache.size > 1000) {
    const now = Date.now();
    // Convert entries to array for compatibility with older TypeScript targets
    const entries = Array.from(memoryCache.entries());
    for (const [k, v] of entries) {
      if (v.expiresAt <= now) {
        memoryCache.delete(k);
      }
    }
  }
}

/**
 * Get cached image from Cache API (browser cache)
 */
async function getFromCacheAPI(authenticatedUrl: string): Promise<CachedImage | null> {
  if (!cacheApi) return null;
  
  try {
    const response = await cacheApi.match(authenticatedUrl);
    if (response) {
      const cached: CachedImage = await response.json();
      if (isCacheValid(cached)) {
        // Also store in memory cache for faster access
        const key = getCacheKey(authenticatedUrl, cached.expiresAt - Date.now());
        setMemoryCache(key, cached);
        return cached;
      } else {
        // Remove expired entry from Cache API
        await cacheApi.delete(authenticatedUrl);
      }
    }
  } catch (error) {
    console.warn('Error reading from Cache API:', error);
  }
  
  return null;
}

/**
 * Store in Cache API (browser cache)
 */
async function setCacheAPI(authenticatedUrl: string, cached: CachedImage): Promise<void> {
  if (!cacheApi) return;
  
  try {
    await cacheApi.put(
      authenticatedUrl,
      new Response(JSON.stringify(cached), {
        headers: { 'Content-Type': 'application/json' },
      })
    );
  } catch (error) {
    console.warn('Error writing to Cache API:', error);
  }
}

/**
 * Get cached signed URL or generate new one
 * 
 * @param authenticatedUrl - The authenticated URL from Supabase
 * @param generateSignedUrl - Function to generate signed URL if cache miss
 * @param expiresIn - Expiration time in seconds (default: 3600)
 * @returns Cached or newly generated signed URL
 */
export async function getCachedSignedUrl(
  authenticatedUrl: string,
  generateSignedUrl: () => Promise<string>,
  expiresIn: number = 3600
): Promise<string> {
  // For non-authenticated URLs, return as is
  if (!authenticatedUrl.includes('/storage/v1/object/authenticated/')) {
    return authenticatedUrl;
  }

  const cacheKey = getCacheKey(authenticatedUrl, expiresIn);
  
  // Check memory cache first (fastest)
  const memoryCached = getFromMemoryCache(cacheKey);
  if (memoryCached) {
    return memoryCached.signedUrl;
  }
  
  // Check Cache API (persistent cache)
  const apiCached = await getFromCacheAPI(authenticatedUrl);
  if (apiCached) {
    return apiCached.signedUrl;
  }
  
  // Cache miss - generate new signed URL
  try {
    const signedUrl = await generateSignedUrl();
    
    // Calculate expiration timestamp
    const expiresAt = Date.now() + (expiresIn * 1000);
    
    const cached: CachedImage = {
      signedUrl,
      expiresAt,
      originalUrl: authenticatedUrl,
    };
    
    // Store in both caches
    setMemoryCache(cacheKey, cached);
    await setCacheAPI(authenticatedUrl, cached);
    
    return signedUrl;
  } catch (error) {
    console.error('Error generating signed URL:', error);
    // Fallback to original URL
    return authenticatedUrl;
  }
}

/**
 * Clear all cached images
 */
export async function clearImageCache(): Promise<void> {
  memoryCache.clear();
  
  if (cacheApi) {
    try {
      const keys = await cacheApi.keys();
      await Promise.all(keys.map((key) => cacheApi!.delete(key)));
    } catch (error) {
      console.warn('Error clearing Cache API:', error);
    }
  }
}

/**
 * Preload and cache an image
 */
export async function preloadImage(
  authenticatedUrl: string,
  generateSignedUrl: () => Promise<string>,
  expiresIn: number = 3600
): Promise<string> {
  return getCachedSignedUrl(authenticatedUrl, generateSignedUrl, expiresIn);
}

